import React, { useState } from 'react';
import {
  faCheck,
  faExclamationCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { Button } from 'js/design_system/Button';
import { AnswerType, UserTaggingQuestion, QuestionType } from './types';
import { useSurveyState } from './SurveyState';
import { Question } from './Question';
import {
  OuterContainer,
  ContentContainer,
  CueContainer,
  ExclamationIcon,
  CueTextContainer,
  QuestionContainer,
  QuestionProgress,
  ButtonContainer,
  CompletedBox,
  CompletedCheckmark,
} from './styles/UserTaggingPingBodyStyles';
// imports for translations:
// @ts-expect-error ts(2307)
import i18nResources from '@alienfast/i18next-loader?include=**/reg_path/signup_to_download/survey/BaseQuestion.json!config/locales_i18next/index.js';
import addResources from 'js/i18next/addResources';
import useTranslationTest from 'js/utils/hooks/useTranslationTest';
import FreeformQuestion from './FreeformQuestion';
import RIQuestion from './RIQuestion';
const TRANSLATIONS_NAMESPACE = 'RegPath.SignupToDownload.BaseQuestion';
addResources(i18nResources, TRANSLATIONS_NAMESPACE);

export enum PingSurveyVariant {
  DownloadDisabled = 'download_disabled',
  NonDownload = 'non_download',
}

type Props = {
  variant: PingSurveyVariant;
  questions: UserTaggingQuestion[];
};

export const PingBody: React.FC<Props> = ({ variant, questions }) => {
  const {
    selectedAnswers,
    selectAnswer,
    questionAnswered,
    getSetQuestionLoadTime,
    customAnswerSetter,
    customAnswers,
    submitCustomAnswers,
    freeformAnswers,
    updateFreeformAnswer,
    submitFreeformAnswers,
    riState,
    submitRIanswers,
  } = useSurveyState(questions);

  // which question the user is on
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // whether the survey has been completed
  const [completed, setCompleted] = useState(false);

  // whether the 'thank you' message after completing the survey has faded yet
  const [completedMessageHidden, setCompletedMessageHidden] = useState(false);

  // for translating question text
  const [t] = useTranslationTest(TRANSLATIONS_NAMESPACE, true);

  const setCompletedMessageToDisappear = () => {
    setTimeout(() => setCompletedMessageHidden(true), 5000);
  };

  const answerSurveyCue = (
    <CueContainer>
      <ExclamationIcon icon={faExclamationCircle} />
      <CueTextContainer>
        <strong style={{ marginBottom: '8px' }}>{t('help')}.</strong>
        {variant === PingSurveyVariant.NonDownload ? (
          <></>
        ) : (
          t('getFreePaper', { count: questions.length })
        )}
      </CueTextContainer>
    </CueContainer>
  );

  const getAnswerType = (question: UserTaggingQuestion) => {
    return question.questionType === QuestionType.SingleSelect ||
      question.questionType === QuestionType.SingleSelectColumns
      ? AnswerType.radio
      : AnswerType.checkbox;
  };

  const questionComponents = questions.map((q, i) => {
    const isFirst = i === 0;
    const isLast = i === questions.length - 1;
    const canClickNext = questionAnswered(i);
    const canSubmit = questionAnswered(questions.length - 1);

    const handleSubmitClick = () => {
      if (canSubmit) {
        setCompleted(true);
        submitCustomAnswers();
        if (q.questionType === QuestionType.Freeform) {
          submitFreeformAnswers('ping-v6', q.prompt);
        }
        if (riState.researchInterests.length > 0) {
          submitRIanswers('ping-v6');
        }
        window.localStorage.setItem('USER_TAGGING_PING_COMPLETED', 'true');
        setCompletedMessageToDisappear();
      }
    };

    const questionImplementation = () => {
      switch (q.questionType) {
        case QuestionType.Freeform:
          return (
            <FreeformQuestion
              question={q}
              answers={freeformAnswers[q.prompt]}
              updateAnswer={(i, answer) =>
                updateFreeformAnswer(q.prompt, i, answer)
              }
              setQuestionLoadTime={getSetQuestionLoadTime(i)}
              dynamic
            />
          );
        case QuestionType.RI:
          return (
            <RIQuestion
              question={q}
              setQuestionLoadTime={getSetQuestionLoadTime(i)}
              selectedResearchInterests={riState.researchInterests}
              onAdd={riState.addResearchInterest}
              onRemove={riState.removeResearchInterest}
            />
          );
        default:
          return (
            <Question
              userTaggingQuestion={q}
              selectAnswer={(choice: number) => {
                selectAnswer(i, choice);
                window.localStorage.setItem(
                  'USER_TAGGING_PING_COMPLETED',
                  'true',
                );
              }}
              selectedAnswers={selectedAnswers[i]}
              setQuestionLoadTime={getSetQuestionLoadTime(i)}
              answerType={getAnswerType(q)}
              setCustomText={(otherText: string) => {
                customAnswerSetter(i, otherText);
              }}
              customAnswer={customAnswers[i]}
            />
          );
      }
    };

    return (
      <>
        {isFirst && answerSurveyCue}
        <QuestionContainer>
          <QuestionProgress>
            {t('progress', {
              currentQuestion: i + 1,
              totalQuestions: questions.length,
            })}
          </QuestionProgress>
          {questionImplementation()}
        </QuestionContainer>
        <ButtonContainer>
          {!isFirst && (
            <Button secondary onClick={() => setCurrentQuestion(i - 1)}>
              {t('back')}
            </Button>
          )}
          {isLast ? (
            <Button
              iconRight={<span>▸</span>}
              disabled={!canSubmit}
              onClick={handleSubmitClick}
            >
              {variant === PingSurveyVariant.NonDownload
                ? t('submitButtonClose')
                : t('sumbitButton2')}
            </Button>
          ) : (
            <Button
              fullWidth
              iconRight={<span>▸</span>}
              disabled={!canClickNext}
              onClick={() => {
                if (canClickNext) {
                  setCurrentQuestion(i + 1);
                  if (q.questionType === QuestionType.Freeform) {
                    submitFreeformAnswers('ping-v6', q.prompt);
                  }
                }
              }}
            >
              {t('next')}
            </Button>
          )}
        </ButtonContainer>
      </>
    );
  });

  const wiggleStyle = `
    .wiggle {
      animation: surveyAttentionGrab 8s normal forwards;
    }
    @-webkit-keyframes surveyAttentionGrab {
      0% {-webkit-transform: rotate(0deg);}
      2% {-webkit-transform: rotate(-1deg);}
      3.5% {-webkit-transform: rotate(1deg);}
      5% {-webkit-transform: rotate(0deg);}
      100% {-webkit-transform: rotate(0deg);}
    }
  `;

  // has an invisible div with a special ID that the download button looks for
  // if this ID is found, clicking the download button is ineffective
  const pingBody = (
    <>
      <style>{wiggleStyle}</style>
      <div
        id="user-tagging-ping-prevent-download"
        style={{ visibility: 'hidden' }}
      />
      <OuterContainer id="ping-survey-container">
        <ContentContainer>
          {questionComponents[currentQuestion]}
        </ContentContainer>
      </OuterContainer>
    </>
  );

  const completedMessage = completedMessageHidden ? (
    <></>
  ) : (
    <CompletedBox>
      <CompletedCheckmark icon={faCheck} />
      {variant === PingSurveyVariant.NonDownload
        ? t('thanksPersonalize')
        : t('thanks')}
    </CompletedBox>
  );

  if (completed) {
    return completedMessage;
  } else {
    return pingBody;
  }
};
